<template>
  <div v-if="mediaSet?.media.length" class="MoodGallery bg-bg-brandSubtle py-48 desk:py-96">
    <div class="layout-container">
      <a id="MoodGallery" />
      <div class="flex flex-col gap-32 desk:gap-48 items-center justify-center">
        <!-- Headline -->
        <div class="flex flex-col items-center justify-center gap-8 desk:gap-12 desktopMaxW-sm">
          <div class="type-headline-xl text-center">
            {{
              $t('moodgallery.headline', { productName: productItem.name })
            }}
          </div>
          <div
            v-if="text"
            class="type-base text-text-subtle text-center"
            v-html="text"
          />
        </div>

        <!-- Gallery -->
        <div
          :class="gridClasses"
          class="w-full grid grid-flow-row-dense gap-8"
        >
          <div
            v-for="(file, index) in mediaSet.media.slice(0,5)"
            :key="index"
            :class="getItemClasses(index)"
            class="bg-bg-subtle w-full relative"
          >
            <NuxtImg
              preset="standard"
              provider="norce"
              loading="lazy"
              :src="file.url"
              class="absolute w-full h-full object-cover"
              fit="cover"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import { ProductModel } from '~/models/product';
import { ProductVariantModel } from '~/models/productVariant';

const props = defineProps<{
  productItem: ProductModel;
  activeVariant: ProductVariantModel,
}>();

const mediaSet = computed(()=> {
  return props.activeVariant.mediaSetByCode('B2CMoodBoardImages');
});

const imageCount = computed(()=> {
  return Math.min(mediaSet.value?.media.length || 0, 5);
});

const text = computed(()=> {
  return props.productItem.parametricByCode('MoodGalleryText')?.value;
});

// Computed grid classes based on number of items
const gridClasses = computed(() => {
  if (imageCount.value === 1) {
    return 'grid-cols-1';
  } else if (imageCount.value === 2) {
    return 'grid-cols-1 desk:grid-cols-2';
  } else if (imageCount.value === 3) {
    return 'grid-cols-2';
  } else if (imageCount.value === 4) {
    return 'grid-cols-2 desk:grid-cols-3';
  } else if (imageCount.value === 5) {
    return 'grid-cols-2 desk:grid-cols-4';
  } else {
    return 'grid-cols-1'; // Default fallback
  }
});

// Function to return the correct item classes based on index
const getItemClasses = (index: number) => {
  if (imageCount.value === 1) {
    return 'col-span-1 row-span-1 aspect-square desk:aspect-[2/1]';
  } else if (imageCount.value === 2) {
    return 'col-span-1 row-span-1 aspect-square';
  } else if (imageCount.value === 3) {
    if (index === 0) {
      return 'col-span-2 desk:col-span-1 row-span-2 aspect-square';
    } else {
      return 'col-span-1 row-span-1 aspect-portrait desk:aspect-auto';
    }
  } else if (imageCount.value === 4) {
    if (index === 0) {
      return 'col-span-2 desk:col-span-1 row-span-1 desk:row-span-2 aspect-square desk:aspect-portrait';
    } else if (index === 1) {
      return 'col-span-1 row-span-1';
    } else if (index === 2) {
      return 'col-span-1 row-span-2 aspect-portrait';
    } else if (index === 3) {
      return 'col-span-1 row-span-1';
    }
  } else if (imageCount.value === 5) {
    if (index === 0) {
      return 'col-span-2 row-span-3 aspect-square';
    } else if (index === 1) {
      return 'col-span-1 row-span-1 aspect-landscape desk:aspect-auto';
    } else if (index === 2) {
      return 'col-span-1 row-span-2 aspect-portrait desk:aspect-auto';
    } else if (index === 3) {
      return 'col-span-1 row-span-2 aspect-portrait desk:aspect-auto';
    } else if (index === 4) {
      return 'col-span-1 row-span-1 aspect-landscape desk:aspect-auto';
    }
  }
  return 'col-span-1 row-span-1 aspect-[2/1]'; // Default fallback
};

</script>
